import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import PayInvoiceModal from '../components/PayInvoiceModal';
import UserPhotoCell from '../components/UserPhotoCell';
import EmailLink from '../components/EmailLink';
import PhoneLink from '../components/PhoneLink';
import { useAuth } from '../context/AuthContext';
import { bedService, floorService, invoiceService, roomService, settingsService, tenantService } from '../services/api';
import { Bed, Floor, Invoice, Room, User } from '../types';
import { defaultPaymentYear, getPaymentYearOptions } from '../utils/paymentYears';
import {
  computeUnpaidRentMonthsBeforeCurrent,
  getCurrentMonthRentStatus,
  groupInvoicesByUserId,
  matchesTenantPaymentFilter,
  tenantHasPendingDues,
  tenantRentAmount,
  TenantPaymentFilter,
} from '../utils/tenantPaymentStatus';
import { lookupAndPrefillTenantPhone, mergeTenantProfileFields } from '../utils/tenantPhonePrefill';
import { formatFloorDisplay, formatTenantListLocation, formatTenantListRoom } from '../utils/locationLabels';
import { filterResidentTenants } from '../utils/residentTenants';
import './Dashboard.css';
import './TenantManagement.css';

type TenantStatusFilter = 'ALL_USERS' | 'ACTIVE' | 'INACTIVE' | 'VACATING';

const PAYMENT_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

type PayInvoiceForm = {
  paymentMonth: string;
  paymentYear: string;
  paymentType: Invoice['paymentType'];
  paidAmount: string;
  balanceAmount: string;
  discount: string;
  dueDate: string;
};

function defaultDueDateYmd(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return toYMD(d);
}

type UnpaidRentRow = {
  month: string;
  year: string;
  periodLabel: string;
  rent: number;
  status: 'Due';
  balanceDue: number;
  existingInvoiceId?: number;
};

function computeUnpaidRentMonths(tenant: User, invoices: Invoice[]): UnpaidRentRow[] {
  return computeUnpaidRentMonthsBeforeCurrent(tenant, invoices).map((row) => ({
    ...row,
    status: 'Due' as const,
  }));
}

function emptyPayForm(tenant: User): PayInvoiceForm {
  const now = new Date();
  const rent = tenant.rent != null && tenant.rent !== '' ? String(tenant.rent) : '';
  return {
    paymentMonth: now.toLocaleString('default', { month: 'long' }),
    paymentYear: defaultPaymentYear(),
    paymentType: 'RENT',
    paidAmount: rent,
    balanceAmount: '',
    discount: '',
    dueDate: '',
  };
}

function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatJoiningDate(v: string | undefined | null): string {
  if (v == null || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function toDateInputValue(val: string | undefined | null): string {
  if (val == null || val === '') return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  return toYMD(d);
}

function tenantIsOnNotice(t: User): boolean {
  return Boolean(t.isNotice);
}

function tenantKycDone(t: User): boolean {
  return Boolean(t.idFrontSide && t.idBackSide);
}

const emptyQuickForm = () => {
  const today = toYMD(new Date());
  return {
    username: '',
    phoneNumber: '',
    floorId: '' as number | '',
    roomId: '' as number | '',
    bedId: '' as number | '',
    rent: '',
    advance: '0',
    dateOfJoining: today,
    paymentDate: today,
  };
};

const formatFloorLine = formatFloorDisplay;

function formatRoomLine(r: Room): string {
  return `Room ${r.roomNumber}`;
}

/** Sort key tail: missing floor/room/bed go last. */
const SORT_MISSING = '\uffff\uffff';

function naturalCmp(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function floorSortLabel(t: User, floorById: Record<number, Floor>): string {
  const fid = t.floor != null && String(t.floor).trim() !== '' ? Number(t.floor) : NaN;
  if (Number.isFinite(fid) && floorById[fid] != null) return String(floorById[fid].floorNumber);
  const raw = String(t.floor ?? '').trim();
  return raw || SORT_MISSING;
}

function roomSortLabel(t: User, roomById: Record<number, Room>): string {
  const rid = t.room != null && String(t.room).trim() !== '' ? Number(t.room) : NaN;
  if (Number.isFinite(rid) && roomById[rid] != null) return String(roomById[rid].roomNumber);
  const raw = String(t.room ?? '').trim();
  return raw || SORT_MISSING;
}

function bedSortLabel(t: User): string {
  const raw = String(t.bed ?? '').trim();
  return raw || SORT_MISSING;
}

function compareTenantsByFloorRoomBed(
  a: User,
  b: User,
  floorById: Record<number, Floor>,
  roomById: Record<number, Room>
): number {
  let d = naturalCmp(floorSortLabel(a, floorById), floorSortLabel(b, floorById));
  if (d !== 0) return d;
  d = naturalCmp(roomSortLabel(a, roomById), roomSortLabel(b, roomById));
  if (d !== 0) return d;
  d = naturalCmp(bedSortLabel(a), bedSortLabel(b));
  if (d !== 0) return d;
  const aid = a.bedId != null ? Number(a.bedId) : NaN;
  const bid = b.bedId != null ? Number(b.bedId) : NaN;
  if (Number.isFinite(aid) && Number.isFinite(bid) && aid !== bid) return aid - bid;
  return a.id - b.id;
}

function sortTenantsByFloorRoomBed(
  list: User[],
  floorById: Record<number, Floor>,
  roomById: Record<number, Room>
): User[] {
  return [...list].sort((a, b) => compareTenantsByFloorRoomBed(a, b, floorById, roomById));
}

function tenantMatchesSearchQuery(
  t: User,
  q: string,
  floorById: Record<number, Floor>,
  roomById: Record<number, Room>
): boolean {
  const name = (t.username || '').toLowerCase();
  const phone = (t.phoneNumber || '').toLowerCase();
  const email = (t.email || '').toLowerCase();
  const floor = floorSortLabel(t, floorById);
  const room = roomSortLabel(t, roomById);
  const bed = bedSortLabel(t);
  const floorHay = floor !== SORT_MISSING ? floor.toLowerCase() : '';
  const roomHay = room !== SORT_MISSING ? room.toLowerCase() : '';
  const bedHay = bed !== SORT_MISSING ? bed.toLowerCase() : '';
  const floorRoomHay =
    floorHay && roomHay ? `${floorHay} - ${roomHay}` : floorHay || roomHay;
  return (
    name.includes(q) ||
    phone.includes(q) ||
    email.includes(q) ||
    floorHay.includes(q) ||
    roomHay.includes(q) ||
    bedHay.includes(q) ||
    floorRoomHay.includes(q)
  );
}

function IconStatUsers() {
  return (
    <svg className="tenants-stat-svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconStatActive() {
  return (
    <svg className="tenants-stat-svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
        stroke="currentColor"
        strokeWidth="1.65"
      />
      <path d="M8 12.5l2.5 2.5L16 9" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconStatKyc() {
  return (
    <svg className="tenants-stat-svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M14 2v6h6M9 15h6M9 11h2" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" />
    </svg>
  );
}

function IconStatNotice() {
  return (
    <svg className="tenants-stat-svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconActPay() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M2 10h20" stroke="currentColor" strokeWidth="1.75" />
      <path d="M6 15h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function IconActView() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function IconActEdit() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconActNotice() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 11v2a1 1 0 0 0 1 1h1.28l1.42 4.26a1 1 0 0 0 .95.69h7.7a1 1 0 0 0 .95-.69L17.72 14H19a1 1 0 0 0 1-1v-2l-8-4-8 4z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M11 6V4a1 1 0 0 1 1-1h0a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function IconActCheckout() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconActCheckin() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M3 12h12"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.75" />
      <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function IconSliders() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M9 8h6M15 16h6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconExport() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 15V3M8 7l4-4 4 4M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TenantsStatCard({
  accent,
  value,
  label,
  icon,
}: {
  accent: string;
  value: number;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="tenants-stat-card" style={{ ['--t-stat-accent' as string]: accent }}>
      <div className="tenants-stat-card-inner">
        <div className="tenants-stat-card-text">
          <span className="tenants-stat-value">{value}</span>
          <span className="tenants-stat-label">{label}</span>
        </div>
        <div className="tenants-stat-icon-wrap">{icon}</div>
      </div>
    </div>
  );
}

const TenantManagement: React.FC = () => {
  const { hostel, user: authUser, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<TenantStatusFilter>('ACTIVE');
  const [query, setQuery] = useState('');
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickForm, setQuickForm] = useState(emptyQuickForm);
  const [quickPhoneHint, setQuickPhoneHint] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [allFloors, setAllFloors] = useState<Floor[]>([]);
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [roomBeds, setRoomBeds] = useState<Bed[]>([]);
  const [tenantDefaults, setTenantDefaults] = useState<{
    hasDefault: boolean;
    defaultNewTenantAdvance: number;
  } | null>(null);
  const [layoutFloorsList, setLayoutFloorsList] = useState<Floor[]>([]);
  const [layoutRoomsList, setLayoutRoomsList] = useState<Room[]>([]);
  const [noticeTenant, setNoticeTenant] = useState<User | null>(null);
  const [noticeNotified, setNoticeNotified] = useState('');
  const [noticeVacating, setNoticeVacating] = useState('');
  const [noticeSubmitting, setNoticeSubmitting] = useState(false);
  const [floorSearch, setFloorSearch] = useState('');
  const [roomSearch, setRoomSearch] = useState('');
  const [floorListOpen, setFloorListOpen] = useState(false);
  const [roomListOpen, setRoomListOpen] = useState(false);
  const floorBlurT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const roomBlurT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [kycFilter, setKycFilter] = useState<'all' | 'done' | 'pending'>('all');
  const [paymentFilter, setPaymentFilter] = useState<TenantPaymentFilter>('all');
  const [invoicesByUser, setInvoicesByUser] = useState<Map<number, Invoice[]>>(new Map());
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [payTenant, setPayTenant] = useState<User | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'daily_guests') {
      navigate('/daily-guests', { replace: true });
      return;
    }
    if (tab === 'leads') {
      navigate('/leads', { replace: true });
    }
  }, [searchParams, navigate]);

  const stats = useMemo(() => {
    const alive = tenants.filter((t) => !t.isDeleted);
    const total = alive.length;
    const activeUsers = alive.filter((t) => t.isActive);
    const active = activeUsers.length;
    const kycPending = activeUsers.filter((t) => !tenantKycDone(t)).length;
    const onNotice = activeUsers.filter((t) => tenantIsOnNotice(t)).length;
    return { total, active, kycPending, onNotice };
  }, [tenants]);

  const statusFiltered = useMemo(() => {
    const alive = tenants.filter((t) => !t.isDeleted);
    if (status === 'ALL_USERS') return alive;
    if (status === 'ACTIVE') return alive.filter((t) => t.isActive);
    if (status === 'INACTIVE') return alive.filter((t) => !t.isActive);
    if (status === 'VACATING') return alive.filter((t) => tenantIsOnNotice(t));
    return alive;
  }, [tenants, status]);

  const floorById = useMemo(
    () => Object.fromEntries(layoutFloorsList.map((f) => [f.id, f])) as Record<number, Floor>,
    [layoutFloorsList]
  );
  const roomById = useMemo(
    () => Object.fromEntries(layoutRoomsList.map((r) => [r.id, r])) as Record<number, Room>,
    [layoutRoomsList]
  );

  const filtered = useMemo(() => {
    let list = statusFiltered;
    if (kycFilter === 'done') list = list.filter((t) => tenantKycDone(t));
    if (kycFilter === 'pending') list = list.filter((t) => !tenantKycDone(t));
    if (paymentFilter !== 'all' && !invoicesLoading) {
      list = list.filter((t) => matchesTenantPaymentFilter(t, paymentFilter, invoicesByUser));
    }
    const q = query.trim().toLowerCase();
    if (!q) return sortTenantsByFloorRoomBed(list, floorById, roomById);
    const searched = list.filter((t) => tenantMatchesSearchQuery(t, q, floorById, roomById));
    return sortTenantsByFloorRoomBed(searched, floorById, roomById);
  }, [query, statusFiltered, kycFilter, paymentFilter, invoicesByUser, invoicesLoading, floorById, roomById]);

  const hasActiveFilters = kycFilter !== 'all' || paymentFilter !== 'all';

  const roomLabel = (t: User) => formatTenantListRoom(t, { floorById, roomById });
  const locationLabel = (t: User) =>
    formatTenantListLocation(t, { floorById, roomById });

  const rentAmountCell = (t: User) => {
    if (t.isDeleted || !t.isActive) {
      return <span className="tenants-rent-amount tenants-rent-amount--muted">—</span>;
    }
    const rent = tenantRentAmount(t);
    if (rent <= 0) {
      return <span className="tenants-rent-amount tenants-rent-amount--muted">—</span>;
    }
    const invs = invoicesByUser.get(t.id) || [];
    const status = getCurrentMonthRentStatus(t, invs);

    let variant: 'paid' | 'due' | 'unpaid' = 'unpaid';
    let title = 'Rent not paid for current month';
    if (status?.isFullyPaid) {
      variant = 'paid';
      title = `${status.periodLabel} — paid`;
    } else if (status && status.balanceDue > 0.01) {
      variant = 'due';
      title = `${status?.periodLabel ?? 'Current month'} — ₹${Math.round(status?.balanceDue ?? 0).toLocaleString('en-IN')} due`;
    }

    return (
      <span className={`tenants-rent-amount tenants-rent-amount--${variant}`} title={title}>
        ₹{Math.round(rent).toLocaleString('en-IN')}
      </span>
    );
  };

  const kycInfo = (t: User) => {
    const done = tenantKycDone(t);
    return done ? { text: 'Done', done: true } : { text: 'Pending', done: false };
  };

  const formatAdvanceCell = (t: User) => {
    const a = t.advance;
    if (a === undefined || a === null || a === '') return '—';
    const n = Number(a);
    return Number.isFinite(n) ? n.toLocaleString('en-IN') : String(a);
  };

  const roomsOnFloor = useMemo(() => {
    if (quickForm.floorId === '') return [];
    const fid = Number(quickForm.floorId);
    return allRooms.filter((r) => Number(r.floorId) === fid);
  }, [allRooms, quickForm.floorId]);

  const filteredFloors = useMemo(() => {
    const s = floorSearch.trim().toLowerCase();
    let list = allFloors;
    if (s) {
      list = allFloors.filter((fl) => {
        const num = String(fl.floorNumber).toLowerCase();
        const name = (fl.floorName || '').toLowerCase();
        return num.includes(s) || name.includes(s) || String(fl.id) === s;
      });
    }
    return list.slice(0, 30);
  }, [allFloors, floorSearch]);

  const filteredRooms = useMemo(() => {
    if (quickForm.floorId === '') return [];
    const s = roomSearch.trim().toLowerCase();
    let list = roomsOnFloor;
    if (s) {
      list = roomsOnFloor.filter(
        (r) => String(r.roomNumber).toLowerCase().includes(s) || String(r.id) === s
      );
    }
    return list.slice(0, 30);
  }, [roomsOnFloor, roomSearch, quickForm.floorId]);

  const loadTenants = async () => {
    if (!hostel?.id) return;
    try {
      setLoading(true);
      const data = await tenantService.getTenantsByHostel(hostel.id, 'ALL_USERS');
      setTenants(filterResidentTenants(data, authUser?.id));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load tenants:', e);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const loadHostelInvoices = async () => {
    if (!hostel?.id) return;
    try {
      setInvoicesLoading(true);
      const data = await invoiceService.getAllInvoices();
      setInvoicesByUser(groupInvoicesByUserId(data));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load invoices:', e);
      setInvoicesByUser(new Map());
    } finally {
      setInvoicesLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
    void loadHostelInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostel?.id]);

  useEffect(() => {
    if (!hostel?.id) return;
    Promise.all([floorService.getFloorsByHostel(hostel.id), roomService.getRoomsByHostel(hostel.id)])
      .then(([floors, rooms]) => {
        setLayoutFloorsList(floors.filter((f) => f.isActive !== false));
        setLayoutRoomsList(rooms.filter((r) => r.isActive !== false));
      })
      .catch(() => {
        setLayoutFloorsList([]);
        setLayoutRoomsList([]);
      });
  }, [hostel?.id]);

  useEffect(() => {
    return () => {
      if (floorBlurT.current) clearTimeout(floorBlurT.current);
      if (roomBlurT.current) clearTimeout(roomBlurT.current);
    };
  }, []);

  useEffect(() => {
    if (!quickCreateOpen || quickForm.roomId === '') {
      setRoomBeds([]);
      return;
    }
    const rid = Number(quickForm.roomId);
    if (!Number.isFinite(rid)) return;
    bedService
      .getBedsByRoom(rid)
      .then((beds) => setRoomBeds(beds.filter((b) => b.bedStatus === 'AVAILABLE')))
      .catch(() => {
        setRoomBeds([]);
        toast.error('Could not load beds for room');
      });
  }, [quickCreateOpen, quickForm.roomId]);

  const openQuickUser = async () => {
    if (!hostel?.id) return;
    try {
      const [d, floors, rooms] = await Promise.all([
        settingsService.getTenantDefaults(hostel.id),
        floorService.getFloorsByHostel(hostel.id),
        roomService.getRoomsByHostel(hostel.id),
      ]);
      setTenantDefaults({
        hasDefault: d.hasDefault,
        defaultNewTenantAdvance: d.defaultNewTenantAdvance ?? 0,
      });
      setAllFloors(floors.filter((f) => f.isActive !== false));
      setAllRooms(rooms.filter((r) => r.isActive !== false));
      const today = toYMD(new Date());
      setQuickForm({
        ...emptyQuickForm(),
        advance: String(d.defaultNewTenantAdvance ?? 0),
        dateOfJoining: today,
        paymentDate: today,
      });
      setRoomBeds([]);
      setFloorSearch('');
      setRoomSearch('');
      setFloorListOpen(false);
      setRoomListOpen(false);
      setQuickCreateOpen(true);
      if (!d.hasDefault) {
        toast.info('Configure a default password under Settings before saving a Quick user.');
      }
    } catch {
      toast.error('Could not open Quick user');
    }
  };

  const closeQuickModal = () => {
    if (createSubmitting) return;
    setQuickCreateOpen(false);
    setQuickPhoneHint('');
    setFloorSearch('');
    setRoomSearch('');
    setFloorListOpen(false);
    setRoomListOpen(false);
  };

  const selectQuickFloor = (fl: Floor) => {
    setQuickForm((f) => ({
      ...f,
      floorId: fl.id,
      roomId: '',
      bedId: '',
      rent: '',
    }));
    setFloorSearch(formatFloorLine(fl));
    setRoomSearch('');
    setFloorListOpen(false);
    setRoomListOpen(false);
  };

  const selectQuickRoom = (r: Room) => {
    setQuickForm((f) => ({
      ...f,
      roomId: r.id,
      bedId: '',
      rent: '',
    }));
    setRoomSearch(formatRoomLine(r));
    setRoomListOpen(false);
  };

  const openFloorList = () => {
    if (floorBlurT.current) clearTimeout(floorBlurT.current);
    setFloorListOpen(true);
  };

  const openRoomList = () => {
    if (roomBlurT.current) clearTimeout(roomBlurT.current);
    setRoomListOpen(true);
  };

  const scheduleCloseFloorList = () => {
    if (floorBlurT.current) clearTimeout(floorBlurT.current);
    floorBlurT.current = setTimeout(() => setFloorListOpen(false), 150);
  };

  const scheduleCloseRoomList = () => {
    if (roomBlurT.current) clearTimeout(roomBlurT.current);
    roomBlurT.current = setTimeout(() => setRoomListOpen(false), 150);
  };

  const handleCheckout = async (tenant: User) => {
    if (!tenant?.id) return;
    if (invoicesLoading) {
      toast.info('Loading payment data… try again in a moment.');
      return;
    }
    const label = tenant.username || tenant.phoneNumber || 'this tenant';
    const invs = invoicesByUser.get(tenant.id) || [];
    const confirmMessage = tenantHasPendingDues(tenant, invs)
      ? `${label} has pending dues. Do you still want to checkout?`
      : `Checkout ${label}?`;
    const ok = window.confirm(confirmMessage);
    if (!ok) return;
    try {
      await tenantService.checkoutTenant(tenant.id);
      await loadTenants();
      toast.success('Tenant checked out');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Checkout failed:', e);
      toast.error('Checkout failed');
    }
  };

  const openPayModal = (t: User) => setPayTenant(t);

  const closePayModal = () => setPayTenant(null);

  const openNoticeModal = (t: User) => {
    setNoticeTenant(t);
    setNoticeNotified(toDateInputValue(t.notifiedDate ?? null));
    setNoticeVacating(toDateInputValue(t.leavingDate ?? null));
  };

  const dismissNoticeModal = () => {
    if (noticeSubmitting) return;
    setNoticeTenant(null);
    setNoticeNotified('');
    setNoticeVacating('');
  };

  const resetNoticeModalOnly = () => {
    setNoticeTenant(null);
    setNoticeNotified('');
    setNoticeVacating('');
  };

  const handleSaveNotice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id || !noticeTenant?.id) return;
    if (!noticeNotified.trim() || !noticeVacating.trim()) {
      toast.error('Notify date and vacating date are required.');
      return;
    }
    setNoticeSubmitting(true);
    try {
      await tenantService.updateTenantNotice(hostel.id, noticeTenant.id, {
        notifiedDate: noticeNotified,
        leavingDate: noticeVacating,
      });
      toast.success('Notice saved.');
      resetNoticeModalOnly();
      await loadTenants();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save notice');
    } finally {
      setNoticeSubmitting(false);
    }
  };

  const handleRemoveNotice = async () => {
    if (!hostel?.id || !noticeTenant?.id) return;
    const ok = window.confirm('Remove vacating notice for this tenant?');
    if (!ok) return;
    setNoticeSubmitting(true);
    try {
      await tenantService.updateTenantNotice(hostel.id, noticeTenant.id, { clear: true });
      toast.success('Notice removed.');
      resetNoticeModalOnly();
      await loadTenants();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to remove notice');
    } finally {
      setNoticeSubmitting(false);
    }
  };

  const suggestRentForBed = (bed: Bed, room: Room | undefined) => {
    const fromBed = bed.rentPerMonth ?? bed.rentPerWeek ?? bed.rentPerDay;
    const fromRoom = room?.rentPerBed;
    const n = fromBed ?? fromRoom ?? 0;
    return Number.isFinite(Number(n)) ? Number(n) : 0;
  };

  const handleQuickPhoneLookup = async () => {
    if (!hostel?.id) return;
    const phone = quickForm.phoneNumber.trim();
    if (phone.replace(/\D/g, '').length < 8) return;
    setQuickPhoneHint('');
    const result = await lookupAndPrefillTenantPhone(hostel.id, phone, (profile, meta) => {
      if (meta.alreadyInHostel) return;
      setQuickPhoneHint(meta.hint || '');
      setQuickForm((f) => mergeTenantProfileFields(f, profile, ['username', 'phoneNumber']));
    });
    if (result?.alreadyInHostel) {
      setQuickPhoneHint(result.hint || 'This user is already part of your property.');
    }
  };

  const handleQuickBedChange = (bedIdStr: string) => {
    if (bedIdStr === '') {
      setQuickForm((f) => ({ ...f, bedId: '', rent: '' }));
      return;
    }
    const bid = Number(bedIdStr);
    const bed = roomBeds.find((b) => b.id === bid);
    const room = allRooms.find((r) => r.id === Number(quickForm.roomId));
    const suggested = bed ? suggestRentForBed(bed, room) : 0;
    setQuickForm((f) => ({
      ...f,
      bedId: bid,
      rent: String(suggested),
    }));
  };

  const handleCreateQuick = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    if (!tenantDefaults?.hasDefault) {
      toast.error('Set a default password under Settings to create a Quick user.');
      return;
    }
    if (!quickForm.username.trim() || !quickForm.phoneNumber.trim()) {
      toast.error('Full name and phone number are required.');
      return;
    }
    if (quickForm.floorId === '' || quickForm.roomId === '' || quickForm.bedId === '') {
      toast.error('Floor, room, and bed are required.');
      return;
    }
    if (!quickForm.dateOfJoining?.trim() || !quickForm.paymentDate?.trim()) {
      toast.error('Date of joining and payment date are required.');
      return;
    }
    setCreateSubmitting(true);
    try {
      await tenantService.createTenant(hostel.id, {
        quickUser: true,
        username: quickForm.username.trim(),
        phoneNumber: quickForm.phoneNumber.trim(),
        bedId: Number(quickForm.bedId),
        rent: Number(quickForm.rent) || 0,
        advance: Number(quickForm.advance) || 0,
        dateOfJoining: quickForm.dateOfJoining,
        paymentDate: quickForm.paymentDate,
      });
      toast.success(
        'User saved. Existing tenants keep their password; new tenants use the default from Settings.',
      );
      setQuickForm(emptyQuickForm());
      setFloorSearch('');
      setRoomSearch('');
      setFloorListOpen(false);
      setRoomListOpen(false);
      setQuickCreateOpen(false);
      await loadTenants();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create tenant');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const safeHostelExportSlug = () => (hostel?.name || 'users').replace(/[^\w\-]+/g, '_').slice(0, 48);

  const buildExportRows = (): string[][] => {
    const header = ['Name', 'Phone', 'Email', 'Room', 'Bed', 'KYC', 'Advance', 'Joining date', 'Status', 'Vacating notice'];
    return [
      header,
      ...filtered.map((t) => [
        String(t.username || ''),
        String(t.phoneNumber || ''),
        String(t.email || ''),
        roomLabel(t),
        t.bed != null ? String(t.bed) : '',
        tenantKycDone(t) ? 'Done' : 'Pending',
        formatAdvanceCell(t),
        formatJoiningDate(t.dateOfJoining),
        t.isDeleted ? 'Deleted' : t.isActive ? 'Active' : 'Inactive',
        tenantIsOnNotice(t) ? 'Yes' : 'No',
      ]),
    ];
  };

  const downloadExportExcel = () => {
    if (!hostel) return;
    if (filtered.length === 0) {
      toast.error('No users to export.');
      return;
    }
    try {
      const aoa = buildExportRows();
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Users');
      XLSX.writeFile(wb, `${safeHostelExportSlug()}-users.xlsx`);
      toast.success('Excel file downloaded');
      setExportModalOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Could not create Excel file.');
    }
  };

  const downloadExportPdf = () => {
    if (!hostel) return;
    if (filtered.length === 0) {
      toast.error('No users to export.');
      return;
    }
    try {
      const aoa = buildExportRows();
      const head = aoa[0];
      const body = aoa.slice(1);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(11);
      doc.setTextColor(55, 71, 79);
      doc.text(`${hostel.name} — Users`, 40, 28);
      autoTable(doc, {
        startY: 38,
        head: [head],
        body,
        styles: { fontSize: 7, cellPadding: 2.5, textColor: [55, 71, 79] },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 28, right: 28 },
      });
      doc.save(`${safeHostelExportSlug()}-users.pdf`);
      toast.success('PDF downloaded');
      setExportModalOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Could not create PDF.');
    }
  };

  if (!hostel) {
    return (
      <div className="dashboard-container tenants-page tenants-dash">
        <div className="tenants-table-wrap tenants-empty">
          {isSuperAdmin() ? (
            <p>
              Select a property from <strong>Hostels</strong> (open a hostel and use it as the active context), then
              return here to manage tenants.
            </p>
          ) : (
            <p>Loading property…</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container tenants-page tenants-dash">
      <header className="tenants-dash-header">
        <div className="tenants-dash-header-text">
          <h1 className="tenants-dash-title">Tenants</h1>
        </div>
        <div className="tenants-dash-header-actions">
          <button type="button" className="tenants-dash-btn tenants-dash-btn--primary" onClick={openQuickUser}>
            Quick user
          </button>
          <button type="button" className="tenants-dash-btn tenants-dash-btn--secondary" onClick={() => navigate('/tenants/create')}>
            Create user
          </button>
        </div>
      </header>

      <section className="tenants-stat-row" aria-label="User summary">
        <TenantsStatCard accent="#111827" value={stats.total} label="Total users" icon={<IconStatUsers />} />
        <TenantsStatCard accent="#16a34a" value={stats.active} label="Active" icon={<IconStatActive />} />
        <TenantsStatCard accent="#2563eb" value={stats.kycPending} label="KYC pending" icon={<IconStatKyc />} />
        <TenantsStatCard accent="#dc2626" value={stats.onNotice} label="On vacating notice" icon={<IconStatNotice />} />
      </section>

      <div className="tenants-toolbar-card">
        <div className="tenants-toolbar-row">
          <div className="tenants-toolbar-left">
            <label className="tenants-search-field">
              <span className="tenants-search-icon" aria-hidden>
                <IconSearch />
              </span>
              <input
                type="search"
                className="tenants-toolbar-search"
                placeholder="Search by name, phone, email, or room…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search users"
              />
            </label>
            <button
              type="button"
              className={`tenants-toolbar-btn${filterOpen || hasActiveFilters ? ' tenants-toolbar-btn--active' : ''}`}
              onClick={() => setFilterOpen((o) => !o)}
              aria-expanded={filterOpen}
            >
              <IconSliders /> Filter
            </button>
            <select
              className="tenants-toolbar-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as TenantStatusFilter)}
              aria-label="User status"
            >
              <option value="ALL_USERS">All users</option>
              <option value="ACTIVE">Active users</option>
              <option value="INACTIVE">Inactive users</option>
              <option value="VACATING">Vacating users</option>
            </select>
          </div>
          <div className="tenants-toolbar-right">
            <button type="button" className="tenants-toolbar-export" onClick={() => setExportModalOpen(true)}>
              <IconExport /> Export
            </button>
          </div>
        </div>
        {filterOpen ? (
          <div className="tenants-filter-panel">
            <div className="tenants-filter-row">
              <span className="tenants-filter-heading">KYC documents</span>
              <div className="tenants-filter-seg" role="group" aria-label="KYC filter">
                <button type="button" className={kycFilter === 'all' ? 'is-active' : ''} onClick={() => setKycFilter('all')}>
                  All
                </button>
                <button type="button" className={kycFilter === 'done' ? 'is-active' : ''} onClick={() => setKycFilter('done')}>
                  Done
                </button>
                <button type="button" className={kycFilter === 'pending' ? 'is-active' : ''} onClick={() => setKycFilter('pending')}>
                  Pending
                </button>
              </div>
            </div>
            <div className="tenants-filter-row">
              <span className="tenants-filter-heading">Payment</span>
              <div className="tenants-filter-pills" role="group" aria-label="Payment filter">
                <button
                  type="button"
                  className={paymentFilter === 'all' ? 'is-active' : ''}
                  onClick={() => setPaymentFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={paymentFilter === 'current_paid' ? 'is-active' : ''}
                  onClick={() => setPaymentFilter('current_paid')}
                  disabled={invoicesLoading}
                >
                  Current month paid
                </button>
                <button
                  type="button"
                  className={paymentFilter === 'current_unpaid' ? 'is-active' : ''}
                  onClick={() => setPaymentFilter('current_unpaid')}
                  disabled={invoicesLoading}
                >
                  Current month unpaid
                </button>
                <button
                  type="button"
                  className={paymentFilter === 'due' ? 'is-active' : ''}
                  onClick={() => setPaymentFilter('due')}
                  disabled={invoicesLoading}
                >
                  Due
                </button>
                <button
                  type="button"
                  className={paymentFilter === 'prev_unpaid' ? 'is-active' : ''}
                  onClick={() => setPaymentFilter('prev_unpaid')}
                  disabled={invoicesLoading}
                >
                  Previous month unpaid
                </button>
                <button
                  type="button"
                  className={paymentFilter === 'advance_unpaid' ? 'is-active' : ''}
                  onClick={() => setPaymentFilter('advance_unpaid')}
                  disabled={invoicesLoading}
                >
                  Advance unpaid
                </button>
              </div>
              {invoicesLoading ? <span className="tenants-filter-hint">Loading payment data…</span> : null}
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                className="tenants-filter-reset"
                onClick={() => {
                  setKycFilter('all');
                  setPaymentFilter('all');
                }}
              >
                Reset filters
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="loading-container tenants-dash-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="tenants-table-wrap tenants-empty">
          {tenants.filter((t) => !t.isDeleted).length > 0 ? (
            <>
              <p>No users match the current filters.</p>
              <p className="tenants-empty-hint">
                {status === 'ACTIVE'
                  ? 'Pending or inactive accounts are hidden — try “All users” or “Inactive users”.'
                  : 'Clear search and payment/KYC filters, or check the Onboarding tab for pending applications.'}
              </p>
            </>
          ) : (
            <p>
              No tenants yet for this property. Use <strong>Quick user</strong> or <strong>Create user</strong>, or
              approve applicants under <strong>Onboarding</strong>.
            </p>
          )}
        </div>
      ) : (
        <div className="tenants-table-card">
          <div className="tenants-table-meta">
            <span className="tenants-table-count">
              <strong>{filtered.length}</strong> tenant{filtered.length === 1 ? '' : 's'}
            </span>
            {hasActiveFilters ? <span className="tenants-table-filtered">Filters applied</span> : null}
          </div>
          <div className="tenants-table-wrap responsive-table-wrap">
          <table className="tenants-table responsive-table">
            <thead>
              <tr>
                <th className="tenants-col-idx">#</th>
                <th className="tenants-col-location">Room</th>
                <th>Tenant</th>
                <th>Phone</th>
                <th className="tenants-col-rent">Rent</th>
                <th>KYC</th>
                <th>Advance</th>
                <th className="tenants-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, idx) => {
                const kyc = kycInfo(t);
                const onNotice = tenantIsOnNotice(t);
                return (
                  <tr key={t.id} className={t.isDeleted ? 'tenants-row--deleted' : onNotice ? 'tenants-row--notice' : ''}>
                    <td className="tenants-col-idx responsive-table__hide-mobile" data-label="#">
                      {idx + 1}
                    </td>
                    <td className="tenants-col-location" data-label="Room">
                      <span className="tenants-location-chip" title={locationLabel(t)}>
                        {roomLabel(t)}
                      </span>
                    </td>
                    <td className="tenants-col-name responsive-table__primary" data-label="Tenant">
                      <div className="tenants-name-block">
                        <div className="user-list-name-cell">
                          <UserPhotoCell name={t.username} photoUrl={t.photo} size="sm" />
                          <div className="tenants-name-text">
                            <span className="tenants-name-primary" title={t.username}>
                              {t.username || '—'}
                            </span>
                            {t.email ? (
                              <EmailLink email={t.email} className="tenants-name-email" />
                            ) : null}
                          </div>
                        </div>
                        {onNotice ? <span className="tenants-notice-tag">Vacating</span> : null}
                        {t.isDeleted ? <span className="tenants-status-tag tenants-status-tag--deleted">Deleted</span> : null}
                        {!t.isDeleted && !t.isActive ? (
                          <span className="tenants-status-tag tenants-status-tag--inactive">Inactive</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="tenants-col-phone" data-label="Phone">
                      <PhoneLink phone={t.phoneNumber} className="tenants-phone-link" />
                    </td>
                    <td className="tenants-col-rent" data-label="Rent">
                      {rentAmountCell(t)}
                    </td>
                    <td data-label="KYC">
                      <span className={`tenants-kyc-badge${kyc.done ? ' tenants-kyc-badge--done' : ''}`}>
                        {kyc.text}
                      </span>
                    </td>
                    <td className="tenants-col-advance" data-label="Advance">
                      {formatAdvanceCell(t) === '—' ? '—' : `₹${formatAdvanceCell(t)}`}
                    </td>
                    <td className="tenants-col-actions responsive-table__actions" data-label="Actions">
                      <div className="tenants-actions">
                        <button
                          type="button"
                          className="tenants-act-btn tenants-act-btn--pay"
                          disabled={t.isDeleted}
                          onClick={() => openPayModal(t)}
                          title="Pay"
                        >
                          <span className="tenants-act-btn__icon" aria-hidden>
                            <IconActPay />
                          </span>
                          <span className="tenants-act-btn__label">Pay</span>
                        </button>
                        <button
                          type="button"
                          className="tenants-act-btn tenants-act-btn--view"
                          onClick={() => navigate(`/tenants/view/${t.id}`)}
                          title="View"
                        >
                          <span className="tenants-act-btn__icon" aria-hidden>
                            <IconActView />
                          </span>
                          <span className="tenants-act-btn__label">View</span>
                        </button>
                        <button
                          type="button"
                          className="tenants-act-btn tenants-act-btn--edit"
                          disabled={t.isDeleted}
                          onClick={() => navigate(`/tenants/edit/${t.id}`)}
                          title="Edit"
                        >
                          <span className="tenants-act-btn__icon" aria-hidden>
                            <IconActEdit />
                          </span>
                          <span className="tenants-act-btn__label">Edit</span>
                        </button>
                        <button
                          type="button"
                          className="tenants-act-btn tenants-act-btn--notice"
                          disabled={t.isDeleted}
                          onClick={() => openNoticeModal(t)}
                          title="Notice"
                        >
                          <span className="tenants-act-btn__icon" aria-hidden>
                            <IconActNotice />
                          </span>
                          <span className="tenants-act-btn__label">Notice</span>
                        </button>
                        {!t.isDeleted && !t.isActive ? (
                          <button
                            type="button"
                            className="tenants-act-btn tenants-act-btn--checkin"
                            onClick={() => navigate(`/tenants/edit/${t.id}?checkIn=1`)}
                            title="Check in"
                          >
                            <span className="tenants-act-btn__icon" aria-hidden>
                              <IconActCheckin />
                            </span>
                            <span className="tenants-act-btn__label">Check in</span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="tenants-act-btn tenants-act-btn--checkout"
                          disabled={!t.isActive || t.isDeleted}
                          onClick={() => t.isActive && !t.isDeleted && handleCheckout(t)}
                          title="Checkout"
                        >
                          <span className="tenants-act-btn__icon" aria-hidden>
                            <IconActCheckout />
                          </span>
                          <span className="tenants-act-btn__label">Checkout</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <PayInvoiceModal
        tenant={payTenant}
        onClose={closePayModal}
        onSuccess={() => void loadHostelInvoices()}
      />

      {exportModalOpen ? (
        <div className="tenants-notice-overlay" role="presentation" onClick={() => setExportModalOpen(false)}>
          <div
            className="tenants-notice-card tenants-export-card"
            role="dialog"
            aria-labelledby="tenants-export-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tenants-notice-header">
              <h3 id="tenants-export-title">Export users</h3>
              <button type="button" className="tenants-notice-close" aria-label="Close" onClick={() => setExportModalOpen(false)}>
                ×
              </button>
            </div>
            <p className="tenants-export-sub">
              Choose a format. The file includes the current table ({filtered.length} user{filtered.length === 1 ? '' : 's'}) with filters and
              floor–room–bed sort applied.
            </p>
            <div className="tenants-export-actions">
              <button type="button" className="tenants-export-btn tenants-export-btn--excel" onClick={downloadExportExcel} disabled={filtered.length === 0}>
                Excel (.xlsx)
              </button>
              <button type="button" className="tenants-export-btn tenants-export-btn--pdf" onClick={downloadExportPdf} disabled={filtered.length === 0}>
                PDF
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {noticeTenant && (
        <div
          className="tenants-notice-overlay"
          role="presentation"
          onClick={() => !noticeSubmitting && dismissNoticeModal()}
        >
          <div className="tenants-notice-card" role="dialog" aria-labelledby="notice-modal-title" onClick={(e) => e.stopPropagation()}>
            <div className="tenants-notice-header">
              <h3 id="notice-modal-title">Vacating notice</h3>
              <button
                type="button"
                className="tenants-notice-close"
                aria-label="Close"
                disabled={noticeSubmitting}
                onClick={dismissNoticeModal}
              >
                ×
              </button>
            </div>
            <p className="tenants-notice-tenant-name">{noticeTenant.username || noticeTenant.phoneNumber || 'Tenant'}</p>

            {tenantIsOnNotice(noticeTenant) && (
              <p className="tenants-notice-badge">On notice — you can update dates or remove the notice.</p>
            )}

            <form className="tenants-notice-body" onSubmit={handleSaveNotice}>
              <div className="tenants-notice-fields">
                <label className="tenants-notice-field">
                  <span>Notify date</span>
                  <input
                    type="date"
                    required
                    className="tenants-notice-input"
                    value={noticeNotified}
                    onChange={(e) => setNoticeNotified(e.target.value)}
                  />
                </label>
                <label className="tenants-notice-field">
                  <span>Vacating date</span>
                  <input
                    type="date"
                    required
                    className="tenants-notice-input"
                    value={noticeVacating}
                    onChange={(e) => setNoticeVacating(e.target.value)}
                  />
                </label>
              </div>
              <div className="tenants-notice-footer tenants-notice-footer--split">
                {tenantIsOnNotice(noticeTenant) && (
                  <button
                    type="button"
                    className="tenants-notice-btn tenants-notice-btn-remove"
                    disabled={noticeSubmitting}
                    onClick={handleRemoveNotice}
                  >
                    {noticeSubmitting ? 'Please wait…' : 'Remove notice'}
                  </button>
                )}
                <div className="tenants-notice-footer-right">
                  <button type="submit" className="tenants-notice-btn tenants-notice-btn-primary" disabled={noticeSubmitting}>
                    {noticeSubmitting ? 'Saving…' : 'Save notice'}
                  </button>
                  <button
                    type="button"
                    className="tenants-notice-btn tenants-notice-btn-secondary"
                    disabled={noticeSubmitting}
                    onClick={dismissNoticeModal}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {quickCreateOpen && (
        <div
          className="tenants-quick-overlay"
          role="presentation"
          onClick={() => !createSubmitting && closeQuickModal()}
        >
          <div
            className="tenants-quick-card"
            role="dialog"
            aria-labelledby="quick-tenant-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="tenants-quick-header">
              <h2 id="quick-tenant-title" className="tenants-quick-title">
                Create User
              </h2>
              <button
                type="button"
                className="tenants-quick-close"
                aria-label="Close"
                disabled={createSubmitting}
                onClick={closeQuickModal}
              >
                ×
              </button>
            </div>
            <p className="tenants-quick-hint">
              <strong>Required:</strong> full name, phone, floor, room, bed, due date, and date of joining. Password
              comes from <strong>Settings</strong> automatically.
              {tenantDefaults?.hasDefault ? '' : ' Set a default password in Settings before saving.'}
            </p>
            <form onSubmit={handleCreateQuick}>
              <div className="tenants-quick-field">
                <label htmlFor="quick-tenant-name" className="tenants-quick-label">
                  Full Name <span className="tenants-req-star">*</span>
                </label>
                <input
                  id="quick-tenant-name"
                  className="tenants-quick-input"
                  required
                  placeholder="Enter Full Name"
                  value={quickForm.username}
                  onChange={(e) => setQuickForm((f) => ({ ...f, username: e.target.value }))}
                />
              </div>
              <div className="tenants-quick-field">
                <label htmlFor="quick-tenant-phone" className="tenants-quick-label">
                  Phone Number <span className="tenants-req-star">*</span>
                </label>
                <input
                  id="quick-tenant-phone"
                  className="tenants-quick-input"
                  required
                  placeholder="Enter Phone Number"
                  type="tel"
                  value={quickForm.phoneNumber}
                  onChange={(e) => {
                    setQuickPhoneHint('');
                    setQuickForm((f) => ({ ...f, phoneNumber: e.target.value }));
                  }}
                  onBlur={() => void handleQuickPhoneLookup()}
                />
                <span className="tenants-quick-field-hint">Tab out after phone to load existing profile.</span>
              </div>

              {quickPhoneHint ? (
                <p
                  className={`tenants-quick-lookup-banner${
                    quickPhoneHint.toLowerCase().includes('already part') ? ' tenants-quick-lookup-banner--warn' : ''
                  }`}
                >
                  {quickPhoneHint}
                </p>
              ) : null}

              <div className="tenants-quick-grid tenants-quick-grid--3">
                <div className="tenants-quick-field tenants-quick-field--combo">
                  <span className="tenants-quick-label">
                    Floor <span className="tenants-req-star">*</span>
                  </span>
                  <input
                    id="quick-floor"
                    type="text"
                    className="tenants-quick-input"
                    autoComplete="off"
                    placeholder="Floor"
                    aria-required="true"
                    value={floorSearch}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFloorSearch(v);
                      setRoomSearch('');
                      openFloorList();
                      setQuickForm((f) => ({
                        ...f,
                        floorId: '',
                        roomId: '',
                        bedId: '',
                        rent: '',
                      }));
                    }}
                    onFocus={openFloorList}
                    onBlur={scheduleCloseFloorList}
                  />
                  {floorListOpen && (
                    <ul className="tenants-quick-suggest-list">
                      {filteredFloors.length === 0 ? (
                        <li style={{ padding: '0.5rem', color: '#888', fontSize: '0.9rem' }}>No matching floors</li>
                      ) : (
                        filteredFloors.map((fl) => (
                          <li key={fl.id}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectQuickFloor(fl);
                              }}
                            >
                              {formatFloorLine(fl)}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
                <div className="tenants-quick-field tenants-quick-field--combo">
                  <span className="tenants-quick-label">
                    Room <span className="tenants-req-star">*</span>
                  </span>
                  <input
                    id="quick-room"
                    type="text"
                    className="tenants-quick-input"
                    autoComplete="off"
                    disabled={quickForm.floorId === ''}
                    placeholder="Room"
                    aria-required="true"
                    value={roomSearch}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRoomSearch(v);
                      openRoomList();
                      setQuickForm((f) => ({
                        ...f,
                        roomId: '',
                        bedId: '',
                        rent: '',
                      }));
                    }}
                    onFocus={() => {
                      if (quickForm.floorId !== '') openRoomList();
                    }}
                    onBlur={scheduleCloseRoomList}
                  />
                  {roomListOpen && quickForm.floorId !== '' && (
                    <ul className="tenants-quick-suggest-list">
                      {filteredRooms.length === 0 ? (
                        <li style={{ padding: '0.5rem', color: '#888', fontSize: '0.9rem' }}>No matching rooms</li>
                      ) : (
                        filteredRooms.map((r) => (
                          <li key={r.id}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectQuickRoom(r);
                              }}
                            >
                              {formatRoomLine(r)}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
                <div className="tenants-quick-field">
                  <label htmlFor="quick-bed" className="tenants-quick-label">
                    Bed <span className="tenants-req-star">*</span>
                  </label>
                  <select
                    id="quick-bed"
                    className="tenants-quick-input"
                    required
                    disabled={quickForm.roomId === ''}
                    value={quickForm.bedId === '' ? '' : String(quickForm.bedId)}
                    onChange={(e) => handleQuickBedChange(e.target.value)}
                  >
                    <option value="">Bed</option>
                    {roomBeds.map((b) => (
                      <option key={b.id} value={b.id}>
                        Bed {b.bedNumber}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="tenants-quick-grid tenants-quick-grid--2">
                <div className="tenants-quick-field">
                  <label htmlFor="quick-rent" className="tenants-quick-label">
                    Rent Amount
                  </label>
                  <input
                    id="quick-rent"
                    type="number"
                    className="tenants-quick-input"
                    min={0}
                    step="1"
                    placeholder="Rent"
                    value={quickForm.rent}
                    onChange={(e) => setQuickForm((f) => ({ ...f, rent: e.target.value }))}
                  />
                </div>
                <div className="tenants-quick-field">
                  <label htmlFor="quick-advance" className="tenants-quick-label">
                    Advance Amount
                  </label>
                  <input
                    id="quick-advance"
                    type="number"
                    className="tenants-quick-input"
                    min={0}
                    step="1"
                    placeholder="Enter Advance Amount"
                    value={quickForm.advance}
                    onChange={(e) => setQuickForm((f) => ({ ...f, advance: e.target.value }))}
                  />
                </div>
              </div>

              <div className="tenants-quick-grid tenants-quick-grid--2">
                <div className="tenants-quick-field">
                  <label htmlFor="quick-due" className="tenants-quick-label">
                    Due Date <span className="tenants-req-star">*</span>
                  </label>
                  <input
                    id="quick-due"
                    type="date"
                    className="tenants-quick-input"
                    required
                    value={quickForm.paymentDate}
                    onChange={(e) => setQuickForm((f) => ({ ...f, paymentDate: e.target.value }))}
                  />
                </div>
                <div className="tenants-quick-field">
                  <label htmlFor="quick-join" className="tenants-quick-label">
                    Date of Joining <span className="tenants-req-star">*</span>
                  </label>
                  <input
                    id="quick-join"
                    type="date"
                    className="tenants-quick-input"
                    required
                    value={quickForm.dateOfJoining}
                    onChange={(e) => setQuickForm((f) => ({ ...f, dateOfJoining: e.target.value }))}
                  />
                </div>
              </div>

              <div className="tenants-quick-footer">
                <button
                  type="button"
                  className="tenants-quick-btn-close"
                  disabled={createSubmitting}
                  onClick={closeQuickModal}
                >
                  Close
                </button>
                <button type="submit" className="tenants-quick-btn-create" disabled={createSubmitting}>
                  {createSubmitting ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default TenantManagement;
